<script setup lang="ts">
import { computed } from 'vue'
import type {
  CreditSearchPreviewCourse,
  CreditSearchPreviewSubject,
} from '@/models/credit-search/preview'
import { previewSubjectSelectionKey } from '@/models/credit-search/selection'

defineOptions({ name: 'CreditSearchSubjectCreditTable' })

const props = defineProps<{
  course: CreditSearchPreviewCourse
  refreshing: boolean
}>()

const emit = defineEmits<{
  remove: [selectionKey: string]
}>()

type SubjectCreditRow = {
  key: string
  subject: CreditSearchPreviewSubject
  showSourceCells: boolean
  sourceRowSpan: number
  sourceGroupLabel: string
  outcome: { subject_code: string; subject_name: string } | null
  conditions: string | null
  conditionsRowSpan: number
  noPrecedent: boolean
}

const rows = computed<SubjectCreditRow[]>(() => {
  const result: SubjectCreditRow[] = []

  for (const [subjectIndex, subject] of props.course.subjects.entries()) {
    const outputRules = subject.matching_requisites
      .map((requisite) => requisite.rule)
      .filter((rule) => rule.filtered_outcomes.length > 0)
    const outputCount = outputRules.reduce((total, rule) => total + rule.filtered_outcomes.length, 0)
    const rowSpan = Math.max(1, outputCount)
    if (outputCount === 0) {
      result.push({
        key: `${subjectIndex}-no-precedent`,
        subject,
        showSourceCells: true,
        sourceRowSpan: rowSpan,
        sourceGroupLabel: '',
        outcome: null,
        conditions: null,
        conditionsRowSpan: 1,
        noPrecedent: true,
      })
      continue
    }

    let firstRow = true
    for (const rule of outputRules) {
      for (const [outcomeIndex, outcome] of rule.filtered_outcomes.entries()) {
        result.push({
          key: `${subjectIndex}-${rule.rule_id}-${outcomeIndex}`,
          subject,
          showSourceCells: firstRow,
          sourceRowSpan: rowSpan,
          sourceGroupLabel: outputCount > 1 ? 'One of' : '',
          outcome,
          conditions: outcomeIndex === 0 ? (rule.other_requirements.trim() || '= NONE =') : null,
          conditionsRowSpan: rule.filtered_outcomes.length,
          noPrecedent: false,
        })
        firstRow = false
      }
    }

  }

  return result
})

function remove(subject: CreditSearchPreviewSubject): void {
  emit('remove', previewSubjectSelectionKey(props.course, subject))
}
</script>

<template>
  <table class="table table-sm PrecedentsTableSubjects">
    <thead>
      <tr>
        <th colspan="3">Subjects Completed</th>
        <th colspan="5">Matching La Trobe Subjects</th>
      </tr>
      <tr>
        <td></td>
        <th>Code</th>
        <td></td>
        <th>Code</th>
        <th>Name</th>
        <th>Conditions</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="row in rows" :key="row.key">
        <td v-if="row.showSourceCells" class="credit-preview-delete-cell" :rowspan="row.sourceRowSpan">
          <button type="button" class="btn btn-danger btn-sm" aria-label="Delete" :disabled="props.refreshing"
            @click="remove(row.subject)">X</button>
        </td>
        <td v-if="row.showSourceCells" :rowspan="row.sourceRowSpan">{{ row.subject.external_subject_code }}</td>
        <td v-if="row.showSourceCells" :rowspan="row.sourceRowSpan">
          <div>{{ row.sourceGroupLabel }}</div>
        </td>
        <template v-if="row.noPrecedent">
          <td colspan="4" class="NoExistingPrecedent">== No Existing Precedent Found ==</td>
        </template>
        <template v-else>
          <td class="OutcomeBox" :title="row.conditions ?? undefined">{{ row.outcome?.subject_code }}</td>
          <td class="OutcomeBox">{{ row.outcome?.subject_name }}</td>
          <td v-if="row.conditions" class="CreditOtherRequirements" :rowspan="row.conditionsRowSpan">
            {{ row.conditions }}
          </td>
        </template>
      </tr>
    </tbody>
  </table>
</template>

<style lang="scss" scoped>
.CreditOtherRequirements:empty::after {
  content: '= NONE =';
  white-space: nowrap;
}

.NoExistingPrecedent {
  background: #ffdddd !important;
  min-height: 2rem;
  text-align: center;
}
</style>
